import React from "react";
import { useRecoilValue } from "recoil";
import {
  Box,
  Typography,
  Stack,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Grid,
  CircularProgress,
} from "@mui/material";
import moment from "moment";
import { consola } from "consola";
import organizationIdSelector from "../../atoms/organizationIdSelector";
import trialDaysSelector from "../../atoms/trialDaysSelector";
import NewColors from "../../themes/NewColors";
import { AccountType } from "../../data/Types";
import { getStripeProducts, createCheckoutSession, updateSubscriptionTier } from "../../data/StripeData";
import SubscriptionTierCard from "./SubscriptionTierCard";

import StripeFormWrapper from "./StripeForm";

function Prices({ products, trial = false }) {
  if (!products) {
    return null;
  }
  const totalPrice = products.prices.reduce((acc, price) => {
    return acc + (price.unit_amount * price.quantity) / 100;
  }, 0);

  // let planMessage = "You are subscribed to the ";
  let totalMessage = "Your monthly charge is ";
  if (trial) {
    // planMessage = "You will be subscribed to the ";
    totalMessage = "Your monthly charge will be ";
  }

  return (
    <Stack>
      <Table style={{ width: 400 }}>
        <TableBody>
          <TableRow>
            <TableCell>Base Plan</TableCell>
            <TableCell>
              ${(products.prices[0].unit_amount / 100).toFixed(2)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Additional Coaches</TableCell>
            <TableCell>
              ${(products.prices[1].unit_amount / 100).toFixed(2)} x{" "}
              {products.prices[1].quantity}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Clients</TableCell>
            <TableCell>
              ${(products.prices[2].unit_amount / 100).toFixed(2)} x{" "}
              {products.prices[2].quantity}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Typography style={{ fontSize: 20, marginTop: 20 }}>
        {totalMessage} ${totalPrice.toFixed(2)} per month.
      </Typography>
    </Stack>
  );
}

function PlanName({ account, trial = false }) {
  const organizationId = useRecoilValue(organizationIdSelector);

  const [products, setProducts] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    getStripeProducts({ organizationId })
      .then((data) => {
        setProducts(data);
        setLoading(false);
      })
      .catch((error) => {
        consola.log("error", error);
        setLoading(false);
      });
  }, [account]);

  if (account.stripeSubscriptionId) {
    return (
      <Stack style={{ marginTop: 12, marginBottom: 12 }}>
        <Typography
          style={{ fontSize: 24, fontWeight: "bold", color: NewColors.brand.primary }}
        >
          {trial ? "  Subscription" : "Subscribed"}
        </Typography>

        {loading && (
          <Typography style={{ fontSize: 18, marginTop: 12 }}>
            Loading plan...
          </Typography>
        )}

        <Prices products={products} trial={trial} />
      </Stack>
    );
  }
  return (
    <Stack style={{ marginTop: 12, marginBottom: 12 }}>
      <Typography style={{ fontSize: 20 }}>No Plan Found</Typography>
    </Stack>
  );
}

function SpecialPricingPlan() {
  const organizationId = useRecoilValue(organizationIdSelector);
  const mailLink = `mailto:support@mighty45.com?subject=Support: subscription help. (${organizationId})`;

  return (
    <Stack style={{ marginTop: 12, marginBottom: 12 }}>
      <Typography
        style={{ fontSize: 24, fontWeight: "bold", color: NewColors.brand.primary }}
      >
        Special Pricing Plan
      </Typography>
      <Typography style={{ fontSize: 20 }}>
        You have been provided special discount pricing.{" "}
        <a target="_blank" href={mailLink} rel="noreferrer">
          Contact us
        </a>{" "}
        for pricing details and to make adjustments.
      </Typography>
    </Stack>
  );
}

function TrialDaysLeft({ account }) {
  const [expireDate, setExpireDate] = React.useState(null);
  const accountData = useRecoilValue(trialDaysSelector);

  React.useEffect(() => {
    const dateStart = new Date(account.dateCreated.toDate());
    setExpireDate(
      moment(dateStart).add(accountData.trialDays, "days").toDate(),
    );
  }, [account, accountData]);

  return (
    <Stack style={{ marginTop: 12, marginBottom: 12 }}>
      <Typography style={{ fontSize: 20, fontWeight: "bold" }}>
        You have {accountData.daysLeft} days left in your trial. It will expire
        on {expireDate?.toLocaleDateString()}.
      </Typography>
    </Stack>
  );
}

function PricingPage({ organizationData }) {
  // All hooks must be called unconditionally at the top level
  const accountData = useRecoilValue(trialDaysSelector);
  const organizationId = useRecoilValue(organizationIdSelector);
  const [loading, setLoading] = React.useState(false);
  const [updatingTier, setUpdatingTier] = React.useState(null);
  const [updateSuccess, setUpdateSuccess] = React.useState(null);
  const [updateError, setUpdateError] = React.useState(null);
  
  const mailLink = `mailto:support@mighty45.com?subject=Support: subscription help. (${organizationId})`;

  if (!organizationData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
        <CircularProgress />
      </Box>
    );
  }

  const { account } = organizationData;

  if (
    account.accountType &&
    account.accountType === AccountType.special_pricing
  ) {
    return (
      <Box>
        <Stack>
          <Typography variant="h4">Subscription Plan</Typography>
          <SpecialPricingPlan />
        </Stack>
      </Box>
    );
  }

  if (
    account.accountType &&
    account.stripeCustomerId &&
    account.accountType === AccountType.stripe
  ) {
    return (
      <Box>
        <Stack>
          <Typography variant="h4">Subscription Plan</Typography>
          <PlanName account={account} />
          <Typography style={{ fontSize: 20 }}>
            If you have any questions, or to cancel or pause your account,
            please{" "}
            <a target="_blank" href={mailLink} rel="noreferrer">
              contact us
            </a>
            .
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Handle free account type - show subscription tier options
  // Also show if they have a stripeCustomerId but no active subscription (canceled checkout)
  const isFreeOrTrial =
    !account ||
    !account.accountType ||
    account.accountType === AccountType.free ||
    account.accountType === AccountType.trial;
  
  const hasStripeCustomerButNoSubscription =
    account?.stripeCustomerId &&
    !account?.stripeSubscriptionId &&
    account?.accountType !== "standard" &&
    account?.accountType !== "pro" &&
    account?.accountType !== "special_pricing";

  if (isFreeOrTrial || hasStripeCustomerButNoSubscription) {
    if (accountData.expired) {
      return (
        <Stack style={{ marginTop: 12, marginBottom: 12 }}>
          <Typography variant="h4">Mighty45 Trial Over</Typography>
          <Typography style={{ fontSize: 20 }}>
            Your trial has expired. Please{" "}
            <a target="_blank" href={mailLink} rel="noreferrer">
              contact us
            </a>{" "}
            to continue using Mighty45.
          </Typography>
        </Stack>
      );
    }

    // Show tiered subscription options for free accounts
    const currentTier = account?.accountType || "free";

    const handleSubscribe = async (tier) => {
      if (tier === "free") {
        return; // Free tier is already active
      }

      setLoading(true);
      try {
        const result = await createCheckoutSession({
          organizationId,
          tier,
        });

        if (result.status === "OK" && result.url) {
          // Redirect to Stripe Checkout
          window.location.href = result.url;
        } else {
          throw new Error(result.error || "Failed to create checkout session");
        }
      } catch (error) {
        consola.error("Error creating checkout session:", error);
        alert(`Failed to start checkout: ${error.message}`);
        setLoading(false);
      }
    };

    const freeFeatures = [
      "Up to 5 active clients",
      "Basic dashboard",
      "Client management",
      "Coach management",
      "Basic reports",
      "Custom training plans",
      "Meal plans",
      "Text messaging",
    ];

    const standardFeatures = [
      "Everything in Free",
      "Up to 30 active clients",
    ];

    const proFeatures = [
      "Everything in Standard",
      "Up to 100 active clients",
      "All advanced AI features",
      "Training plan imports",
      "AI progress photo analysis",
      "AI insights",
      "Training plan data entry assistance",
      "Meal plan data entry assistance",
      "Organization API access",
      "Priority support",
    ];

    return (
      <Box>
        <Stack spacing={3}>
          <Typography variant="h4" style={{ textAlign: "center" }}>
            Choose Your Plan
          </Typography>
          <Typography
            variant="body1"
            style={{ textAlign: "center", color: "text.secondary", mb: 2 }}
          >
            Select the plan that best fits your coaching business needs. Need more
            than 100 active clients? Contact us for special pricing.
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={6} md={4}>
              <SubscriptionTierCard
                tier="free"
                price="0"
                features={freeFeatures}
                isCurrentTier={currentTier === "free"}
                onSubscribe={() => handleSubscribe("free")}
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SubscriptionTierCard
                tier="standard"
                price="49.95"
                features={standardFeatures}
                isCurrentTier={currentTier === "standard"}
                isPopular={true}
                onSubscribe={() => handleSubscribe("standard")}
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SubscriptionTierCard
                tier="pro"
                price="99.95"
                features={proFeatures}
                isCurrentTier={currentTier === "pro"}
                onSubscribe={() => handleSubscribe("pro")}
                loading={loading}
              />
            </Grid>
          </Grid>
        </Stack>
      </Box>
    );
  }

  // Handle standard and pro account types - show tier cards with upgrade/downgrade
  if (
    account.accountType === "standard" ||
    account.accountType === "pro"
  ) {
    const currentTier = account.accountType;
    
    const freeFeatures = [
      "Up to 5 active clients",
      "Basic dashboard",
      "Client management",
      "Coach management",
      "Basic reports",
      "Custom training plans",
      "Meal plans",
      "Text messaging",
    ];

    const standardFeatures = [
      "Everything in Free",
      "Up to 30 active clients",
    ];

    const proFeatures = [
      "Everything in Standard",
      "Up to 100 active clients",
      "All advanced AI features",
      "Training plan imports",
      "AI progress photo analysis",
      "AI insights",
      "Training plan data entry assistance",
      "Meal plan data entry assistance",
      "Organization API access",
      "Priority support",
    ];

    const handleTierChange = async (tier) => {
      if (tier === currentTier) {
        return; // Already on this tier
      }

      setUpdatingTier(tier);
      setUpdateError(null);
      setUpdateSuccess(null);

      try {
        const result = await updateSubscriptionTier({
          organizationId,
          tier,
        });

        if (result.status === "OK") {
          let successMessage = "";
          if (tier === "free") {
            successMessage = "Your subscription will be canceled and you'll move to the Free plan at the end of your current billing period.";
          } else {
            successMessage = `Your subscription will change to ${tier === "pro" ? "Pro" : "Standard"} at the end of your current billing period.`;
          }
          setUpdateSuccess(successMessage);
          
          // Clear success message after 10 seconds
          setTimeout(() => {
            setUpdateSuccess(null);
          }, 10000);
        } else {
          throw new Error(result.error || "Failed to update subscription");
        }
      } catch (error) {
        consola.error("Error updating subscription tier:", error);
        setUpdateError(error.message || "Failed to update subscription. Please try again.");
      } finally {
        setUpdatingTier(null);
      }
    };

    return (
      <Box>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4">Your Subscription</Typography>
            <Typography variant="h6" style={{ color: NewColors.brand.primary, marginTop: 1 }}>
              {currentTier === "pro" ? "Pro Plan" : "Standard Plan"} - ${currentTier === "pro" ? "99.95" : "49.95"}/month
            </Typography>
          </Box>

          {updateSuccess && (
            <Box
              sx={{
                p: 2,
                backgroundColor: "#e8f5e9",
                borderRadius: 1,
                border: "1px solid #4caf50",
              }}
            >
              <Typography variant="body1" style={{ color: "#2e7d32" }}>
                {updateSuccess}
              </Typography>
            </Box>
          )}

          {updateError && (
            <Box
              sx={{
                p: 2,
                backgroundColor: "#ffebee",
                borderRadius: 1,
                border: "1px solid #f44336",
              }}
            >
              <Typography variant="body1" style={{ color: "#c62828" }}>
                {updateError}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" style={{ color: "text.secondary" }}>
            Changes to your subscription will take effect at the end of your current billing period. You can upgrade, downgrade, or cancel anytime.
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={6} md={4}>
              <SubscriptionTierCard
                tier="free"
                price="0"
                features={freeFeatures}
                isCurrentTier={currentTier === "free"}
                onSubscribe={() => handleTierChange("free")}
                loading={updatingTier === "free"}
                isActiveSubscription={true}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SubscriptionTierCard
                tier="standard"
                price="49.95"
                features={standardFeatures}
                isCurrentTier={currentTier === "standard"}
                isPopular={true}
                onSubscribe={() => handleTierChange("standard")}
                loading={updatingTier === "standard"}
                isActiveSubscription={true}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SubscriptionTierCard
                tier="pro"
                price="99.95"
                features={proFeatures}
                isCurrentTier={currentTier === "pro"}
                onSubscribe={() => handleTierChange("pro")}
                loading={updatingTier === "pro"}
                isActiveSubscription={true}
              />
            </Grid>
          </Grid>

          <Typography variant="body2" style={{ textAlign: "center", marginTop: 2, color: "text.secondary" }}>
            Have questions?{" "}
            <a target="_blank" href={mailLink} rel="noreferrer">
              Contact us
            </a>
          </Typography>
        </Stack>
      </Box>
    );
  }

  return null;
}

export default PricingPage;
